import type { TaskStatus } from '@nexelem/graphne-api';
import { getDuration, useDateFormatter } from '@nexelem/utils';
import { useCustomTranslation } from '../../../../../../hooks';
import { formatUnit } from '../../../Kanban/components/KanbanTask';
import { startedStatuses } from '../../../../../../pages/generic/Products/components/TaskDetailDrawer/constants';
import type { GanttShowOnTaskOptions } from '../../../components/SettingsDrawer/useProductionPlanSettings.types';
import { NodePopoverRow } from './NodePopoverRow';

type NodeBatchPopoverTask = {
  id: number | string;
  name: string;
  total: number;
  unit: string;
  externalId?: string;
  productionOrderNumber?: string;
  dueDate?: string;
};

type NodeBatchPopoverContentProps = {
  id: number | string;
  flags: { toPublish: boolean; frozen: boolean; overdue: boolean; retooling: boolean };
  startDate: Date;
  endDate: Date;
  status: TaskStatus;
  tasks: NodeBatchPopoverTask[];
  showOnTask: GanttShowOnTaskOptions;
};

export const NodeBatchPopoverContent = ({
  id,
  flags,
  startDate,
  endDate,
  status,
  tasks,
  showOnTask,
}: NodeBatchPopoverContentProps) => {
  const { tWrapper: tDrawer } = useCustomTranslation('products.productionPlan.taskDetailsDrawer');
  const { tWrapper: tLegend } = useCustomTranslation('products.productionPlan.gantt.legend');
  const formatDateTime = useDateFormatter({ displayWithHours: true });
  const formatDate = useDateFormatter({});

  const hasStarted = startedStatuses.includes(status);

  const activeFlagLabels = [
    !flags.toPublish && tLegend('unpublished'),
    flags.frozen && tLegend('frozen'),
    flags.overdue && tLegend('overdue'),
    flags.retooling && tLegend('retooling'),
  ].filter(Boolean);

  const showDates = showOnTask.time;
  const showQuantity = showOnTask.progress;
  const showErpOrderNumber = showOnTask.productionOrderExternalNumber;
  const showProductionOrderNumber = showOnTask.productionOrderNumber;
  const showDueDate = showOnTask.dueDate;

  return (
    <div className="flex min-w-65 flex-col text-t12-sb">
      <span>
        {tDrawer('batchNumber')}: {id}
      </span>
      {activeFlagLabels.length > 0 && <span>({activeFlagLabels.join(', ')})</span>}
      {showDates && (
        <ul className="list-inside list-[square] pl-1">
          <NodePopoverRow
            label={hasStarted ? tDrawer('productionStart') : tDrawer('estimatedStart')}
            value={formatDateTime(startDate)}
          />
          <NodePopoverRow
            label={hasStarted ? tDrawer('productionEnd') : tDrawer('estimatedEnd')}
            value={formatDateTime(endDate)}
          />
          <NodePopoverRow
            label={hasStarted ? tDrawer('productionDuration') : tDrawer('productionEstimatedDuration')}
            value={getDuration(startDate, endDate, ['seconds'])}
          />
        </ul>
      )}

      <ol className="mt-4 flex flex-col gap-4">
        {tasks.map((task) => (
          <li className="list-inside list-decimal pl-1 text-t12-b" key={task.id}>
            {task.name}
            <ul className="list-inside list-[square] pl-3 text-t12-sb">
              {showQuantity && (
                <NodePopoverRow label={tDrawer('amount')} value={`${formatUnit(task.total)} ${task.unit}`} />
              )}
              {showProductionOrderNumber && task.productionOrderNumber && (
                <NodePopoverRow label={tDrawer('orderId')} value={task.productionOrderNumber} />
              )}
              {showErpOrderNumber && task.externalId && (
                <NodePopoverRow label={tDrawer('erpNumber')} value={task.externalId} />
              )}
              {showDueDate && task.dueDate && (
                <NodePopoverRow label={tDrawer('due')} value={formatDate(task.dueDate)} />
              )}
            </ul>
          </li>
        ))}
      </ol>
    </div>
  );
};
